import React, { useCallback } from "react";
import { Button } from "components/ui/Button";
import { MoreVerticalIcon } from "components/icons/Icons";
import { Menu, MenuItems, MenuItem, MenuButton } from "@headlessui/react";
import { createIcon } from "components/icons/IconFactory";

export interface ActionMenuItemConfig {
  id: string;
  label: string;
  icon?: string;
  onClick: (data: { id: string; [key: string]: any }) => void;
  shortcut?: string;
  disabled?: boolean;
  variant?: 'default' | 'danger';
  data?: { [key: string]: any }; // Données supplémentaires à passer au onClick
}

export interface DropdownButtonProps {
  items: ActionMenuItemConfig[];
  trigger?: React.ReactNode;
  triggerProps?: {
    color?: string;
    round?: boolean;
    size?: string;
    variant?: string;
  };
  menuClassName?: string;
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  width?: string;
}

export const DropdownButton: React.FC<DropdownButtonProps> = ({
  items,
  trigger,
  triggerProps = { color: "slate", round: true },
  menuClassName,
  anchor = "left",
  width = "w-52"
}) => {
  // Espacement selon l'anchor
  const getAnchorGap = () => {
    switch (anchor) {
      case 'top':
      case 'bottom':
        return '[--anchor-gap:8px]';
      case 'left':
      case 'right':
        return '[--anchor-gap:8px]';
      default:
        return '[--anchor-gap:8px]';
    }
  };

  const defaultMenuClassName = `${width} relative rounded-xl border border-gray-200 bg-white p-1.5 text-sm/6 text-black transition duration-100 ease-out ${getAnchorGap()} focus:outline-none data-closed:scale-95 data-closed:opacity-0 shadow-lg overflow-visible`;

  const renderIcon = useCallback((icon: string) => {
    const Icon = createIcon(icon)
    return <Icon className="w-4" />
  }, []);

  const handleItemClick = useCallback((item: ActionMenuItemConfig) => {
    const clickData = {
      id: item.id,
      ...item.data
    };
    item.onClick(clickData);
  }, []);

  const getItemClassName = (variant?: string, disabled?: boolean) => {
    let baseClass = "group relative flex w-full items-center gap-2 rounded-lg px-3 py-1.5 transition-all duration-150 ease-in-out transform";

    if (disabled) {
      baseClass += " opacity-50 cursor-not-allowed";
    } else {
      // Effets hover normaux
      baseClass += " hover:scale-[1.02] active:scale-[0.98]";

      if (variant === 'danger') {
        baseClass += " text-red-600 hover:bg-red-50 hover:text-red-700 data-focus:bg-red-50 hover:shadow-sm";
      } else {
        baseClass += " hover:bg-primary hover:text-white data-focus:bg-black/10 hover:shadow-sm hover:text-gray-900";
      }
    }

    return baseClass;
  };

  const getShortcutClassName = (variant?: string) => {
    const baseClass = "ml-auto hidden font-sans text-xs transition-colors duration-150 group-hover:inline group-data-focus:inline";
    return variant === 'danger'
      ? `${baseClass} text-red-400 group-hover:text-red-500`
      : `${baseClass} text-gray-400 group-hover:text-gray-600`;
  };

  return (
    <Menu>
      <MenuButton
        as={trigger ? 'div' : Button}
        {...(!trigger ? triggerProps : {})}
        className={trigger ? "" : "transition-all duration-150 hover:scale-105 active:scale-95"}
      >
        {trigger || <MoreVerticalIcon />}
      </MenuButton>

      <MenuItems
        transition
        anchor={anchor}
        className={menuClassName || defaultMenuClassName}
      >
        {items.map((item) => (
          <MenuItem key={item.id} disabled={item.disabled}>
            <button
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              className={getItemClassName(item.variant, item.disabled)}
            >
              <>
                {item.icon && (
                  <span className="flex-shrink-0 transition-transform duration-150 group-hover:scale-110">
                    {renderIcon(item.icon)}
                  </span>
                )}
                <span className="flex-1 text-left transition-colors duration-150">
                  {item.label}
                </span>
                {item.shortcut && (
                  <kbd className={getShortcutClassName(item.variant)}>
                    {item.shortcut}
                  </kbd>
                )}
              </>
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
};

export const useActionMenu = () => {
  const createMenuItem = (
    id: string,
    label: string,
    onClick: (data: { id: string; [key: string]: any }) => void,
    options?: Partial<Omit<ActionMenuItemConfig, 'id' | 'label' | 'onClick'>>
  ): ActionMenuItemConfig => ({
    id,
    label,
    onClick,
    ...options
  });

  return { createMenuItem };
};
